import Component from '@glimmer/component';
import { useQuery } from 'glimmer-apollo';
import { LinkTo } from '@ember/routing';
import { ESPORTS_SCORE_STRIP_QUERY } from '../../gql/queries/esports-score-strip';

const isLeader = (leader, side) => leader === side;

// Persistent live score strip (ESPN "BottomLine"): a thin, sticky, horizontally
// scrollable rail of live + recent matches. Renders only from real match data
// and hides itself entirely when there is nothing to show. Scores are derived
// by counting round victors (the backend exposes no stored score).
export default class LiveScoreStrip extends Component {
  query = useQuery(this, () => [ESPORTS_SCORE_STRIP_QUERY]);

  get matches() {
    const all = this.query.data?.matches;
    if (!all) return [];

    // The backend's isLive filter is a no-op and there is no orderBy, so split
    // the batch here. Live floats above recent finals.
    const live = all.filter((m) => m?.isLive);
    const finals = all.filter((m) => m?.isFinal && !m.isLive);

    const seen = new Set();
    const out = [];
    // Live matches float to the top, then recent finals.
    for (const m of [...live, ...finals]) {
      if (!m?.slug || seen.has(m.slug)) continue;
      if (!m.teams || m.teams.length < 2) continue;
      seen.add(m.slug);
      out.push(this.decorate(m));
      if (out.length >= 12) break;
    }
    return out;
  }

  decorate(match) {
    const [teamA, teamB] = match.teams;
    let scoreA = 0;
    let scoreB = 0;
    for (const round of match.rounds ?? []) {
      const victor = round?.victor?.slug;
      if (victor === teamA.slug) scoreA += 1;
      else if (victor === teamB.slug) scoreB += 1;
    }
    const leader = scoreA === scoreB ? null : scoreA > scoreB ? 'a' : 'b';
    return { ...match, teamA, teamB, scoreA, scoreB, leader };
  }

  <template>
    {{! Read the query's reactive state at the top level so the resource stays
        subscribed and the strip appears once data arrives (glimmer-apollo only
        re-links when its loading/data is consumed in a live template branch). }}
    {{#if this.query.loading}}
      {{! nothing while loading — the strip should never flash empty }}
    {{else if this.matches.length}}
      <div
        class='live-score-strip'
        role='region'
        aria-label='Live and recent match scores'
      >
        <div class='live-score-strip-track' tabindex='0'>
          {{#each this.matches as |match|}}
            <LinkTo
              @route='esports.matches.match'
              @model={{match.slug}}
              class='lss-item'
            >
              <div class='lss-status'>
                {{#if match.isLive}}
                  <span class='lss-live-dot' aria-hidden='true'></span>
                  <span class='lss-status-text lss-status-text--live'>LIVE</span>
                {{else}}
                  <span class='lss-status-text'>FINAL</span>
                {{/if}}
              </div>
              <div class='lss-teams'>
                <div class='lss-team {{if (isLeader match.leader "a") "is-leader"}}'>
                  <img class='lss-logo' src={{match.teamA.logo.url}} alt='' loading='lazy' />
                  <span class='lss-name'>{{match.teamA.name}}</span>
                  <span class='lss-score'>{{match.scoreA}}</span>
                </div>
                <div class='lss-team {{if (isLeader match.leader "b") "is-leader"}}'>
                  <img class='lss-logo' src={{match.teamB.logo.url}} alt='' loading='lazy' />
                  <span class='lss-name'>{{match.teamB.name}}</span>
                  <span class='lss-score'>{{match.scoreB}}</span>
                </div>
              </div>
            </LinkTo>
          {{/each}}
        </div>
      </div>
    {{/if}}
  </template>
}
